import { Alert, Button, Card, Form, Typography, message as antdMessage } from "antd";
import { useEffect, useState, type ComponentProps } from "react";
import { useNavigate, useParams } from "react-router";
import { InputNumber as ZhInputNumber } from "zhihao-ui-react";

import request from "@/api";

// beta.13 的类型声明未暴露 controls，保留旧 Vue 数字输入的无步进配置。
type ProjectCustomerInputNumberProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const integerInputProps: ProjectCustomerInputNumberProps = { controls: false, min: 0, precision: 0 };
const quantityInputProps: ProjectCustomerInputNumberProps = { controls: false, min: 1, precision: 0 };
const moneyInputProps: ProjectCustomerInputNumberProps = { controls: false, min: 0, precision: 2 };

export type ProjectAddCustomerData = Record<string, any>;

/** 复用 Vue 项目详情接口，供新增采购商表单回显项目数据。 */
export async function loadProjectForCustomer(id: number | string): Promise<ProjectAddCustomerData> {
    const { data } = await request.service.get<ProjectAddCustomerData>(`/admin/project/vo/${id}`);
    return data;
}

/** 采购项目新增采购商最小闭环，保持 Vue addBuyer 接口的数组 payload。 */
export function ProjectAddCustomerPage({ projectId: projectIdProp }: { projectId?: number | string }) {
    const { projectId: routeProjectId } = useParams<{ projectId?: string }>();
    const projectId = projectIdProp ?? routeProjectId;
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [error, setError] = useState("");

    useEffect(() => {
        if (!projectId) return;
        void loadProjectForCustomer(projectId)
            .then(data => form.setFieldsValue(data.projectExtends?.[0] ?? {}))
            .catch(() => setError("项目详情加载失败"));
    }, [form, projectId]);

    /** 提交前把表单数值统一转为 Vue 扩展实体所需的 number 类型。 */
    const submit = async (values: ProjectAddCustomerData) => {
        setError("");
        const payload = [
            {
                projectId: Number(projectId),
                buyerId: Number(values.buyerId),
                purQuantity: Number(values.purQuantity),
                buyerUnitPrice: Number(values.buyerUnitPrice),
                buyerFrozenAmount: Number(values.buyerFrozenAmount),
                buyerContractAmount: Number(values.buyerContractAmount),
                buyerMinPayLimit: Number(values.buyerMinPayLimit),
                serviceCosts: []
            }
        ];
        try {
            await request.service.post("/admin/project/addBuyer", payload);
            antdMessage.success("采购商添加成功");
            // 对齐旧 Vue onSubmit：新增采购商成功后关闭当前页并返回项目详情。
            navigate(-1);
        } catch {
            // 保存失败时保留当前表单，并提供页面内反馈，避免只依赖短暂 Toast。
            setError("采购商添加失败");
            antdMessage.error("采购商添加失败");
        }
    };

    return (
        <section aria-label="project-add-customer-page">
            <Card>
                <Typography.Title level={4}>新增采购商</Typography.Title>
                {error ? <Alert role="alert" title={error} type="error" /> : null}
                <Form form={form} layout="vertical" onFinish={submit}>
                    <Form.Item
                        label="采购商账号ID"
                        name="buyerId"
                        rules={[{ required: true, message: "请输入采购商账号ID！" }]}
                    >
                        <ZhInputNumber {...integerInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="意向采购吨数"
                        name="purQuantity"
                        rules={[{ required: true, message: "请输入意向采购吨数！" }]}
                    >
                        <ZhInputNumber {...quantityInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="预估单价"
                        name="buyerUnitPrice"
                        rules={[{ required: true, message: "请输入预估单价！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="冻结金额"
                        name="buyerFrozenAmount"
                        rules={[{ required: true, message: "请输入冻结金额！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="合同金额"
                        name="buyerContractAmount"
                        rules={[{ required: true, message: "请输入合同金额！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Form.Item
                        label="最低预付款提醒额度"
                        name="buyerMinPayLimit"
                        rules={[{ required: true, message: "请输入最低预付款提醒额度！" }]}
                    >
                        <ZhInputNumber {...moneyInputProps} />
                    </Form.Item>
                    <Button autoInsertSpace={false} htmlType="submit" type="primary">
                        提交
                    </Button>
                </Form>
                <div className="fixed bottom-[30px] left-[36px]">
                    <Button aria-label="返回" size="large" type="primary" onClick={() => navigate(-1)}>
                        返回
                    </Button>
                </div>
            </Card>
        </section>
    );
}

export default ProjectAddCustomerPage;
